// src/utils/dxfDrawCore.js
import * as THREE from 'three';

import { drawLineFromCode } from '../../commands/drawLineFromCode';
import { drawCircleFromCode } from '../../commands/drawCircleFromCode';
import { drawArcFromCode } from '../../commands/drawArcFromCode';
import { drawEllipticArcFromCode } from '../../commands/drawEllipticArcFromCode';
import { drawEllipseFromCode } from '../../commands/drawEllipseFromCode';
import { drawRectFromCode } from '../../commands/drawRectFromCode';
import { drawPolylineFromCode } from '../../commands/drawPolylineFromCode';
import { drawSplineFromCode } from '../../commands/drawSplineFromCode';
import { drawTextFromCode } from '../../commands/drawTextFromCode';
import { drawMtextFromCode } from '../../commands/drawMtextFromCode';
import { createSolidHatchFromBoundary } from '../../commands/createSolidHatchFromBoundary';
import { generateUniqueId } from '../../utils/generateUniqueId'
import CFB from 'cfb';

/**
 * DXF Arbitrary Axis Algorithm — Extrusion Direction'dan OCS eksenlerini hesaplar.
 * OCS (Object Coordinate System) → WCS (World Coordinate System) dönüşümü için kullanılır.
 * @param {{ x: number, y: number, z: number }} extDir - Extrusion direction vektörü
 * @returns {{ Ax: {x,y,z}, Ay: {x,y,z}, Az: {x,y,z} }} OCS eksenleri (WCS cinsinden)
 */
function _computeOCSAxes(extDir) {
  const len = Math.sqrt(extDir.x * extDir.x + extDir.y * extDir.y + extDir.z * extDir.z);
  if (len < 1e-10) return { Ax: { x: 1, y: 0, z: 0 }, Ay: { x: 0, y: 1, z: 0 }, Az: { x: 0, y: 0, z: 1 } };
  const N = { x: extDir.x / len, y: extDir.y / len, z: extDir.z / len };

  let Ax;
  if (Math.abs(N.x) < 1 / 64 && Math.abs(N.y) < 1 / 64) {
    // WorldY × N
    Ax = { x: N.z, y: 0, z: -N.x };
  } else {
    // WorldZ × N
    Ax = { x: -N.y, y: N.x, z: 0 };
  }
  // Normalize Ax
  const axLen = Math.sqrt(Ax.x * Ax.x + Ax.y * Ax.y + Ax.z * Ax.z);
  Ax = { x: Ax.x / axLen, y: Ax.y / axLen, z: Ax.z / axLen };

  // Ay = N × Ax
  let Ay = {
    x: N.y * Ax.z - N.z * Ax.y,
    y: N.z * Ax.x - N.x * Ax.z,
    z: N.x * Ax.y - N.y * Ax.x,
  };
  const ayLen = Math.sqrt(Ay.x * Ay.x + Ay.y * Ay.y + Ay.z * Ay.z);
  Ay = { x: Ay.x / ayLen, y: Ay.y / ayLen, z: Ay.z / ayLen };

  return { Ax, Ay, Az: N };
}

function convertHAlign(halign) {
  switch (halign) {
    case 0: return 'left';     // Horizontal Left
    case 1: return 'center';   // Center
    case 2: return 'right';    // Right
    default: return 'left';    // Default fallback
  }
}

export function dxfDrawCore(scene, entities = [], blocks = {}, target, layers, parentInsert = null) {
  if (!scene || !Array.isArray(entities)) return;
  const drawingGroup = new THREE.Group();

  // Layer isimlerini onOff durumlarına eşle (görünürlük kontrolü için)
  const layerVisibility = {};
  if (Array.isArray(layers)) {
    layers.forEach(layer => {
      layerVisibility[layer.name] = layer.onOff !== false; // undefined veya true = görünür
    });
  }
  entities.forEach((entity) => {
    const type = entity.type;

    // ✅ Layer "0" inheritance: blok içinde layer="0" ise parent INSERT'in layer'ını kullan
    let effectiveLayerName = entity.layer;
    if (entity.layer === '0' && parentInsert?.layer) {
      effectiveLayerName = parentInsert.layer;
    }

    const layerName = effectiveLayerName;
    const foundLayer = layers
      ? (Array.isArray(layers)
          ? layers.find(layer => layer.name === layerName)
          : Object.values(layers).find(layer => layer.name === layerName))
      : null;
    const layerColor = foundLayer?.color
      ?? foundLayer?.colorIndex
      ?? 0xffffff;

    // ✅ ByBlock (0) renk desteği: entity.color 0 ise parent'tan al
    // ✅ ByLayer (256 veya undefined): layer rengini kullan
    // ✅ TrueColor (420): entity.colorIndex en yüksek öncelik
    let color;
    let isColorByLayer = false;
    if (entity.colorIndex && typeof entity.colorIndex === 'string' && entity.colorIndex.trim()) {
      // TrueColor (group 420) - en yüksek öncelik
      color = entity.colorIndex.trim();
    } else if (entity.color === 0 && parentInsert?.color !== undefined && parentInsert?.color !== null) {
      // ByBlock: parent INSERT'in rengini kullan
      color = parentInsert.color;
    } else if (entity.color === 256 || entity.color === undefined || entity.color === null || entity.color === '') {
      // ByLayer: layer rengini kullan
      color = layerColor;
      isColorByLayer = true;
    } else if (entity.color === 0) {
      // ByBlock ama parent yok: layer rengini kullan
      color = layerColor;
      isColorByLayer = true;
    } else {
      // Entity'nin kendi rengi
      color = entity.color;
    }

    // ✅ Transparency → Three.js opacity dönüşümü
    // Öncelik: 1) Entity'nin kendi transparency'si, 2) Layer transparency
    // DXF: 0 = opak, 255 = tamamen saydam
    // Three.js: 1.0 = opak, 0.0 = tamamen saydam
    const entityTransparency = entity.transparency;
    const layerTransparency = foundLayer?.transparency ?? 0;
    const effectiveTransparency = entityTransparency !== undefined ? entityTransparency : layerTransparency;
    const opacity = 1 - (effectiveTransparency / 255);

 

    // Layer kapalıysa bu entity'yi atla (görünmez yap)
    const isLayerVisible = layerVisibility[layerName] !== false;
     switch (type) {

      case 'LINE': {
        const [start, end] = entity.vertices || [];
        if (start && end) {
          const line = drawLineFromCode(null,
            start,
            end,
            color,
            { layerName, opacity }
          );
          line.visible = isLayerVisible;
          line.userData.source = 'dxf';
          line.userData.isColorByLayer = isColorByLayer;

          if (target === "scene") {
            scene.add(line);
          } else if (target === "group") {
            drawingGroup.add(line);
          }
        }
        break;
      }

      case 'CIRCLE':
        const circle = drawCircleFromCode(null, {
          center: entity.center,
          radius: entity.radius,
          color,
          layerName,
          opacity,
        });
        circle.visible = isLayerVisible;
        circle.userData.source = 'dxf';
        circle.userData.isColorByLayer = isColorByLayer;
        if (target === "scene") {
          scene.add(circle);
        } else if (target === "group") {
          drawingGroup.add(circle);
        }
        break;

      case 'ARC': {
        // Saat yönü belirleme mantığı
        let clockwise = false;
        if (entity.angleLength < 0) {
          clockwise = false;
        } else {
          clockwise = true;
        }
        if (entity.startAngle < entity.endAngle) {
          clockwise = !clockwise;
        }

        const arc = drawArcFromCode(null, {
          center: entity.center,
          radius: entity.radius,
          startAngle: entity.startAngle,
          endAngle: entity.endAngle,
          clockwise,
          color,
          layerName,
          opacity,
        }
        );
        arc.visible = isLayerVisible;
        arc.userData.source = 'dxf';
        arc.userData.isColorByLayer = isColorByLayer;
        if (target === "scene") {
          scene.add(arc);
        } else if (target === "group") {
          drawingGroup.add(arc);
        }
        break;
      }
      case 'ELLIPSE': {
        const isFullEllipse = entity.startAngle === 0 && entity.endAngle === 2 * Math.PI;
        let ellipse
        if (!isFullEllipse) {
          ellipse = drawEllipticArcFromCode(null, {
            center: entity.center,
            majorAxisEndPoint: entity.majorAxisEndPoint,
            axisRatio: entity.axisRatio,
            startAngle: entity.startAngle,
            endAngle: entity.endAngle,
            color,
            layerName,
            opacity,
          });
        } else {
          const majorX = entity.majorAxisEndPoint.x;
          const majorY = entity.majorAxisEndPoint.y;
          const radiusX = Math.sqrt(majorX ** 2 + majorY ** 2);
          const radiusY = radiusX * entity.axisRatio;
          const rotation = Math.atan2(majorY, majorX);

          ellipse = drawEllipseFromCode(null, {
            center: entity.center,
            radiusX,
            radiusY,
            rotation,
            color,
            layerName,
            opacity,
          });
        }
        ellipse.visible = isLayerVisible;
        ellipse.userData.source = 'dxf';
        ellipse.userData.isColorByLayer = isColorByLayer;
        if (target === "scene") {
          scene.add(ellipse);
        } else if (target === "group") {
          drawingGroup.add(ellipse);
        }
        break;
      }
      case 'RECT':

        const rect = drawRectFromCode(null, {
          start: entity.start,
          end: entity.end,
          color,
          layerName,
          opacity,
        });
        rect.visible = isLayerVisible;
        rect.userData.source = 'dxf';
        rect.userData.isColorByLayer = isColorByLayer;
        if (target === "scene") {
          scene.add(rect);
        } else if (target === "group") {
          drawingGroup.add(rect);
        }
        break;
      case 'LWPOLYLINE': {
        let polyline
        if (Array.isArray(entity.vertices) && entity.vertices.length >= 2) {
          polyline = drawPolylineFromCode(null, {
            points: entity.vertices,
            color,
            closed: entity.shape,
            layerName,
            opacity,
          });
        } else {
          console.warn('POLYLINE entity has invalid vertices:', entity);
        }
        if (polyline) {
          polyline.visible = isLayerVisible;
          polyline.userData.source = 'dxf';
          polyline.userData.isColorByLayer = isColorByLayer;
          if (target === "scene") {
            scene.add(polyline);
          } else if (target === "group") {
            drawingGroup.add(polyline);
          }
        }
        break;
      }
      case 'SPLINE': {
        let spline;
        const points = entity.fitPoints || (entity.controlPoints ? entity.controlPoints.map(p => ({ x: p.x, y: p.y })) : []);

        if (Array.isArray(points) && points.length >= 2) {
          spline = drawSplineFromCode(null, {
            points: points,
            color,
            layerName,
            opacity,
          });
        } else {
          console.warn('SPLINE entity has invalid points:', entity);
        }

        if (spline) {
          spline.visible = isLayerVisible;
          spline.userData.source = 'dxf';
          spline.userData.isColorByLayer = isColorByLayer;
          if (target === "scene") {
            scene.add(spline);
          } else if (target === "group") {
            drawingGroup.add(spline);
          }
        }
        break;
      }
      case 'INSERT': {
 

        const block = blocks[entity.name];
        if (!block) {
           return;
        }

        // 🔍 Debug: Blok içindeki entity tiplerini göster
        const entityTypes = block.entities?.map(e => e.type) || [];
        const attdefCount = entityTypes.filter(t => t === 'ATTDEF').length;
 

        const group = new THREE.Group();
        // ✅ INSERT entity bilgisini nested block'lara aktar (ByBlock ve Layer "0" desteği)
        // parentInsert olarak entity'yi geçiriyoruz, böylece nested bloklar:
        // - Layer "0" için: parentInsert.layer kullanır
        // - ByBlock (color=0) için: parentInsert.color kullanır (hesaplanmış renk)
        const insertInfo = {
          layer: layerName,  // effectiveLayerName (inherited olabilir)
          color: color,      // hesaplanmış renk (ByBlock/ByLayer uygulanmış)
        };
        dxfDrawCore(group, block.entities, blocks, "scene", layers, insertInfo);

        // ✅ ATTRIB desteği: INSERT entity'sine bağlı attribute değerlerini çiz
        // ATTRIB'ler ATTDEF'lerin gerçek değerleridir ve INSERT'e bağlıdır

        // 🔍 Debug: INSERT entity'sindeki attribs dizisini kontrol et
 

        // ✅ ATTRIB'ler dünya koordinatlarında (world coords) - doğrudan scene'e eklenmeli
        // Block grubuna eklersek scale ve position tekrar uygulanır, yanlış olur
        if (entity.attribs && Array.isArray(entity.attribs)) {
          entity.attribs.forEach((attrib, index) => {
            // ✅ Boş string kontrolü: text boşsa boş geçsin, default değer kullanma
            const attribText = attrib.text !== undefined && attrib.text !== null ? attrib.text : '';
            // ATTRIB pozisyonu dünya koordinatlarında
            const attribPosition = attrib.position || attrib.startPoint || { x: 0, y: 0, z: 0 };

            // Boş text'leri atla - değer yoksa render etme
            if (attribText.trim() === '') {
               return;
            }
 

            const attribMesh = drawTextFromCode(null, {
              text: attribText,
              position: attribPosition,
              endPoint: attrib.alignmentPoint || attribPosition,
              fontSize: attrib.textHeight || 20,
              color: attrib.color || color,
              alignment: convertHAlign(attrib.horizontalJustification),
              halign: attrib.horizontalJustification,
              valign: attrib.verticalJustification,
              rotation: attrib.rotation || 0,
              layerName: attrib.layer || layerName,
              opacity,
            });

            if (attribMesh) {
              attribMesh.visible = isLayerVisible;
              attribMesh.userData = {
                ...attribMesh.userData,
                type: 'attrib',
                tag: attrib.tag,
                parentInsert: entity.name,
              };
              // ⚠️ ATTRIB'ler dünya koordinatlarında - scene'e ekle, group'a değil!
              scene.add(attribMesh);
            } else {
              console.warn(`⚠️ ATTRIB[${index}] mesh creation failed for tag:`, attrib.tag);
            }
          });
        }

        // ✅ INSERT entity'sinin transparency'sini blok içindeki tüm material'lara uygula
        if (opacity < 1) {
          const blockOpacity = 1 - opacity;
          group.traverse((child) => {
            if (child.material) {
              child.material.transparent = true;
              child.material.opacity = blockOpacity;
              child.material.needsUpdate = true;
            }
            // Troika Text için fillOpacity
            if (child.fillOpacity !== undefined) {
              child.fillOpacity = blockOpacity;
            }
          });
        }

        // Pozisyon, scale ve rotasyon — OCS (Extrusion Direction) desteği ile
        let { x = 0, y = 0, z = 0 } = entity.position || {};
        let xScale = entity.xScale ?? 1;
        let yScale = entity.yScale ?? 1;
        let zScale = entity.zScale ?? 1;
        let rotation = entity.rotation || 0; // derece cinsinden

        const ed = entity.extrusionDirection;
        if (ed && (Math.abs(ed.x) > 1e-6 || Math.abs(ed.y) > 1e-6 || ed.z < 0)) {
          // OCS → WCS dönüşümü: Arbitrary Axis Algorithm
          const ocsAxes = _computeOCSAxes(ed);
          const Ax = ocsAxes.Ax, Ay = ocsAxes.Ay, Az = ocsAxes.Az;

          // OCS→WCS matrisini oluştur: [Ax | Ay | Az]
          const ocsMatrix = new THREE.Matrix4().set(
            Ax.x, Ay.x, Az.x, 0,
            Ax.y, Ay.y, Az.y, 0,
            Ax.z, Ay.z, Az.z, 0,
            0, 0, 0, 1
          );

          // Pozisyonu OCS'den WCS'ye dönüştür
          const wcsPos = new THREE.Vector3(x, y, z).applyMatrix4(ocsMatrix);

          // Yerel döndürme (OCS Z ekseni etrafında)
          const rotRad = THREE.MathUtils.degToRad(rotation);
          const localRotation = new THREE.Matrix4().makeRotationZ(rotRad);

          // Yerel ölçek
          const localScale = new THREE.Matrix4().makeScale(xScale, yScale, zScale);

          // Tam matris: T_wcs × M_ocs × R_local × S_local
          const fullMatrix = new THREE.Matrix4()
            .makeTranslation(wcsPos.x, wcsPos.y, wcsPos.z)
            .multiply(ocsMatrix)
            .multiply(localRotation)
            .multiply(localScale);

          group.matrixAutoUpdate = false;
          group.matrix.copy(fullMatrix);
          group.matrixWorldNeedsUpdate = true;
        } else {
          // Standart dönüşüm (OCS yok veya varsayılan Z+ extrusion)
          group.position.set(x, y, z);
          group.scale.set(xScale, yScale, zScale);
          if (rotation) {
            group.rotation.z = THREE.MathUtils.degToRad(rotation);
          }
        }

        group.userData = {
          id: generateUniqueId("block"),
          type: 'block',
          name: entity.name,
          layerName,
          isSelectable: true,
          source: 'dxf',
          isColorByLayer,
        };
        group.visible = isLayerVisible;

        if (target === "group") {
          drawingGroup.add(group);
        } else if (target === "scene") {
          scene.add(group);
        }
        break;
      }

      case 'TEXT': {

        const text = drawTextFromCode(null, {
          text: entity.text,
          position: entity.startPoint,                  // ✅ DXF verisinden
          endPoint: entity.endPoint || entity.startPoint,                    // ✅ DXF verisinden
          fontSize: entity.textHeight || 20,            // ✅ DXF'de textHeight
          color: entity.color || 0xffffff,
          alignment: convertHAlign(entity.halign),      // ✅ alignment karşılığı
          // font, bold, italic gibi gömülü değilse varsayılana düşer
          halign: entity.halign,
          valign: entity.valign,
          rotation: entity.rotation || 0,
          layerName,
          opacity,
        });
        text.visible = isLayerVisible;
        if (text.userData) {
          text.userData.source = 'dxf';
          text.userData.isColorByLayer = isColorByLayer;
        }
        if (target === "scene") {
          scene.add(text);
        } else if (target === "group") {
          drawingGroup.add(text);
        }
        break;
      }

      case 'MTEXT': {
         const targetForMtext = target === "scene" ? scene : drawingGroup;
         const mtextMesh = drawMtextFromCode(null, {
          TextString: entity.text,
          text: entity.text,
          position: entity.position,
          font: entity.font,
          height: entity.textHeight || entity.height || 20,
          width: entity.width,
          color: entity.color,
          colorIndex: entity.colorIndex,
          layerColor: layerColor, // ByLayer rengi için
          bold: entity.bold,
          italic: entity.italic,
          underline: entity.underline,
          alignment: convertHAlign(entity.halign),
          height: entity.height,
          width: entity.width,
          attachmentPoint: entity.attachmentPoint,
          rotation: entity.rotation,
          directionVector: entity.directionVector,
          drawingDirection: entity.drawingDirection,
          layerName,
          fontPath: entity.fontPath,
          opacity,
        });
        if (mtextMesh) {
          mtextMesh.visible = isLayerVisible;
          if (mtextMesh.userData) {
            mtextMesh.userData.source = 'dxf';
            mtextMesh.userData.isColorByLayer = isColorByLayer;
          }
          // TEXT gibi açıkça sahneye ekle
          if (target === "scene") {
            scene.add(mtextMesh);
          } else if (target === "group") {
            drawingGroup.add(mtextMesh);
          }
        }
        break;
      }

      case 'ATTDEF': {
 

        // ATTDEF render etmiyoruz - sadece tanım
        break;
      }

      case 'solidhatch':
        if (!entity.segments || entity.segments.length === 0) {
          console.warn("HATCH entity has no segments:", entity);
          return;
        }

        // HATCH için Solid veya Desen oluşturma
        const hatch = createSolidHatchFromBoundary(null, {
          segments: entity.segments,
          color,
        });
        if (hatch) {
          hatch.visible = isLayerVisible;
          if (hatch.userData) {
            hatch.userData.source = 'dxf';
            hatch.userData.isColorByLayer = isColorByLayer;
          }
          if (target === "scene") {
            scene.add(hatch);
          } else if (target === "group") {
            drawingGroup.add(hatch);
          }
        }
        break;

      case 'HATCH':
 
        if (!entity.boundaryPath || entity.boundaryPath.length === 0) {
          console.warn("HATCH entity has no boundaryPath:", entity);
          return;
        }

        const boundaryPaths = classifyBoundaryPaths(entity.boundaryPath); // Sadece outer + inner ayrımı yapıyor olmalı

        const hatchMesh = createSolidHatchFromBoundary(entity, {
          boundaryPaths: boundaryPaths,
          color: color,
          pattern: entity.pattern,
          moveX: entity.x || 0,
          moveY: entity.y || 0,
          scene: target === "scene" ? scene : null,
          group: target === "group" ? drawingGroup : null,
        });

        if (hatchMesh) {
          hatchMesh.visible = isLayerVisible;
          if (hatchMesh.userData) {
            hatchMesh.userData.source = 'dxf';
            hatchMesh.userData.isColorByLayer = isColorByLayer;
          }
        } else {
          console.warn("createSolidHatchFromBoundary failed:", entity);
        }

        break;


      case 'OLE2FRAME': {
        const oleHex = entity.binaryHex;
        const hasBinary = oleHex && oleHex.length >= 2 && !entity.noBinary;

        // Mesh boyutu ve konumu — synchronous, hemen hesapla
        const ul = entity.upperLeft  || { x: 0, y: 0, z: 0 };
        const lr = entity.lowerRight || { x: 1, y: -1, z: 0 };
        const oleW  = Math.abs(lr.x - ul.x) || 1;
        const oleH  = Math.abs(ul.y - lr.y) || 1;
        const oleCx = (ul.x + lr.x) / 2;
        const oleCy = (ul.y + lr.y) / 2;

        // Placeholder canvas texture ile mesh hemen oluştur (UI bloke olmaz)
        const oleCanvas  = document.createElement('canvas');
        oleCanvas.width  = 512;
        oleCanvas.height = 512;
        const oleCtx0 = oleCanvas.getContext('2d');
        if (!hasBinary) {
          // Binary yok — net şekilde eksik OLE etiketi
          oleCtx0.fillStyle = '#3a2a2a';
          oleCtx0.fillRect(0, 0, 512, 512);
          oleCtx0.strokeStyle = '#a66';
          oleCtx0.lineWidth = 8;
          oleCtx0.setLineDash([20, 12]);
          oleCtx0.strokeRect(8, 8, 496, 496);
          oleCtx0.fillStyle = '#f88';
          oleCtx0.textAlign = 'center';
          oleCtx0.font = 'bold 36px Arial, sans-serif';
          oleCtx0.fillText('OLE Verisi Yok', 256, 240);
          oleCtx0.font = '24px Arial, sans-serif';
          oleCtx0.fillStyle = '#caa';
          const label = entity.itemName || entity.linkName || 'OLE';
          oleCtx0.fillText(label, 256, 280);
          oleCtx0.fillText('(DXF binary içermiyor)', 256, 312);
        } else {
          oleCtx0.fillStyle = '#d0d0d0';
          oleCtx0.fillRect(0, 0, 512, 512);
        }

        const oleTexture  = new THREE.CanvasTexture(oleCanvas);
        const oleMesh     = new THREE.Mesh(
          new THREE.PlaneGeometry(oleW, oleH),
          new THREE.MeshBasicMaterial({ map: oleTexture, side: THREE.DoubleSide })
        );
        oleMesh.position.set(oleCx, oleCy, ul.z || 0);
        oleMesh.userData.source     = 'dxf';
        oleMesh.userData.type       = 'ole2frame';
        // Re-export için orijinal binary blob ve OLE2FRAME alanlarını sakla.
        // Kullanıcı OLE'yi taşır/ölçeklerse mevcut mesh konumu kullanılır,
        // diğer alanlar (class adı, OLE tipi, orijinal chunk yapısı) aynen
        // aktarılır — yoksa AutoCAD parser uyarı verebiliyor.
        oleMesh.userData.binaryHex  = oleHex;
        oleMesh.userData.hexChunks  = entity.hexChunks || null;
        oleMesh.userData.oleWidth   = oleW;
        oleMesh.userData.oleHeight  = oleH;
        oleMesh.userData.layerName  = entity.layer || '0';
        oleMesh.userData.itemName   = entity.itemName    || 'OLE';
        oleMesh.userData.linkName   = entity.linkName    || 'OLE';
        oleMesh.userData.oleVersion = entity.oleVersion  ?? 2;
        oleMesh.userData.oleType    = entity.oleType     ?? 2;
        oleMesh.userData.plotQuality = entity.plotQuality ?? 0;
        // App-üretimi OLE (ör. Projabze marka): DXF export'ta iç 128-byte header
        // köşeleri canlı bounds'a yeniden yazılsın (yoksa AutoCAD içeriği origine çizer).
        oleMesh.userData.oleAppGenerated = entity.oleAppGenerated || false;
        oleMesh.visible = isLayerVisible;

        // Async decode callback'leri (setTimeout/img.onload) rebuild'den SONRA
        // bitince, dispose edilmiş mesh'e yeni CanvasTexture bağlayıp ORPHAN
        // bırakıyordu (asla dispose edilmez → bellek sızıntısı). Bu helper:
        //  (1) mesh artık sahnede/canlı değilse yeni texture'ı HEMEN dispose edip çıkar,
        //  (2) yeni map'i atarken ESKİ map'i (placeholder veya önceki) dispose eder.
        const applyOleTexture = (newTex) => {
          if (!newTex) return;
          if (!oleMesh.parent || oleMesh.userData.disposed) { newTex.dispose(); return; }
          const old = oleMesh.material.map;
          oleMesh.material.map = newTex;
          oleMesh.material.needsUpdate = true;
          if (old && old !== newTex) old.dispose();
        };

        if (target === 'scene') scene.add(oleMesh);
        else drawingGroup.add(oleMesh);

        // Binary yoksa parse adımını atla — placeholder yeterli
        if (!hasBinary) break;

        // Ağır iş (hex parse + CFB + BMP) event loop'tan sonraya ertele — UI bloke olmaz
        setTimeout(() => {
          try {
            // Hex → Uint8Array (charCode ile — substr/parseInt'ten daha hızlı)
            const len    = oleHex.length >> 1;
            const binary = new Uint8Array(len);
            for (let bi = 0; bi < len; bi++) {
              const c1 = oleHex.charCodeAt(bi * 2);
              const c2 = oleHex.charCodeAt(bi * 2 + 1);
              binary[bi] =
                ((c1 >= 97 ? c1 - 87 : c1 >= 65 ? c1 - 55 : c1 - 48) << 4) |
                 (c2 >= 97 ? c2 - 87 : c2 >= 65 ? c2 - 55 : c2 - 48);
            }

            // CFB magic D0 CF 11 E0 A1 B1 — genellikle 128. byte'ta başlar
            let cfbOff = -1;
            for (let bi = 0; bi <= binary.length - 8; bi++) {
              if (binary[bi] === 0xD0 && binary[bi+1] === 0xCF &&
                  binary[bi+2] === 0x11 && binary[bi+3] === 0xE0 &&
                  binary[bi+4] === 0xA1 && binary[bi+5] === 0xB1) {
                cfbOff = bi;
                break;
              }
            }
            if (cfbOff === -1) return;

            const cfbParsed = CFB.read(binary.slice(cfbOff), { type: 'array' });

            // ---- Yardımcı: bytes'ı blob → <img> → canvas yolu ile oleMesh'e ata ----
            const renderImageBlobBytes = (bytes, mime) => {
              const blob = new Blob([bytes], { type: mime });
              const url  = URL.createObjectURL(blob);
              const img  = new Image();
              img.onload = () => {
                const fc = document.createElement('canvas');
                fc.width  = img.naturalWidth  || 512;
                fc.height = img.naturalHeight || 512;
                fc.getContext('2d').drawImage(img, 0, 0);
                applyOleTexture(new THREE.CanvasTexture(fc));
                URL.revokeObjectURL(url);
              };
              img.onerror = (e) => {
                console.warn('[OLE2FRAME] resim decode hatası:', e);
                URL.revokeObjectURL(url);
              };
              img.src = url;
            };

            // ---- Yardımcı: doğrulamalı PNG/JPG/PDF detect (false-positive azaltır) ----
            const detectImageAtStart = (u8) => {
              if (!u8 || u8.length < 8) return null;
              // PNG: 89 50 4E 47 0D 0A 1A 0A (8 byte unique magic)
              if (u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4E && u8[3] === 0x47 &&
                  u8[4] === 0x0D && u8[5] === 0x0A && u8[6] === 0x1A && u8[7] === 0x0A) {
                return { mime: 'image/png' };
              }
              // JPEG: FF D8 FF + valid marker (E0..EF, DB, C0, C4, FE)
              if (u8[0] === 0xFF && u8[1] === 0xD8 && u8[2] === 0xFF) {
                const m = u8[3];
                if ((m >= 0xE0 && m <= 0xEF) || m === 0xDB || m === 0xC0 || m === 0xC4 || m === 0xFE) {
                  return { mime: 'image/jpeg' };
                }
              }
              return null;
            };

            // ---- Yardımcı: byte dizisinde validate'li image magic ara ----
            const findImageInBytes = (u8, scanLen) => {
              if (!u8 || u8.length < 16) return null;
              const N = Math.min(u8.length - 8, scanLen);
              for (let pi = 0; pi <= N; pi++) {
                // PNG 8-byte magic — false-positive ihtimali çok düşük
                if (u8[pi] === 0x89 && u8[pi+1] === 0x50 && u8[pi+2] === 0x4E && u8[pi+3] === 0x47 &&
                    u8[pi+4] === 0x0D && u8[pi+5] === 0x0A && u8[pi+6] === 0x1A && u8[pi+7] === 0x0A) {
                  return { bytes: u8.slice(pi), mime: 'image/png' };
                }
                // JPEG: FF D8 FF + valid marker
                if (u8[pi] === 0xFF && u8[pi+1] === 0xD8 && u8[pi+2] === 0xFF) {
                  const m = u8[pi+3];
                  if ((m >= 0xE0 && m <= 0xEF) || m === 0xDB || m === 0xC0 || m === 0xC4 || m === 0xFE) {
                    return { bytes: u8.slice(pi), mime: 'image/jpeg' };
                  }
                }
              }
              return null;
            };

            // Görünen seçim boyutu — OlePres000 başlığından (HIMETRIC) oku.
            // Bulunamazsa OLE2FRAME bbox'ından (mm varsayımı) hesapla.
            // Bu boyut, kullanıcı Excel'den kopyalarken seçtiği aralığın render boyutu.
            let viewPxW = oleW * 96 / 25.4;  // mm → px @ 96 DPI
            let viewPxH = oleH * 96 / 25.4;
            try {
              const presE = CFB.find(cfbParsed, '/\x02OlePres000');
              if (presE?.content?.length >= 40) {
                const p = presE.content instanceof Uint8Array
                  ? presE.content : new Uint8Array(Array.from(presE.content));
                const pdv = new DataView(p.buffer, p.byteOffset);
                // OLE Presentation Stream "new" format başlığı:
                //   0: ulNew(0xFFFFFFFF) 4: dwTagLen(0xFFFFFFFF) 8: cfFormat
                //   12: dwAspect 16: dwAdvf 20: lindex
                //   24: extX (HIMETRIC=0.01mm) 28: extY 32: dwReserved 36: dwSize
                if (pdv.getUint32(0, true) === 0xFFFFFFFF &&
                    pdv.getUint32(4, true) === 0xFFFFFFFF) {
                  const extX = pdv.getInt32(24, true);
                  const extY = pdv.getInt32(28, true);
                  if (extX > 0 && extY > 0 && extX < 1e7 && extY < 1e7) {
                    // HIMETRIC → px @ 96 DPI: px = himetric * 96 / 2540
                    viewPxW = extX * 96 / 2540;
                    viewPxH = extY * 96 / 2540;
                  }
                }
              }
            } catch (e) { /* başlık parse hatası — fallback bbox kullanılır */ }

            // Format 1: /CONTENTS (yeni AutoCAD / OLE2FrameService çıktısı — BMP direkt)
            // Format 2: /\x01Ole10Native (eski AutoCAD / OLE 1.0 — BMP veya DIB)
            let bmpBytes = null;
            const contentsEntry = CFB.find(cfbParsed, '/CONTENTS');
            if (contentsEntry?.content?.length >= 16) {
              const c = contentsEntry.content;
              const u8 = c instanceof Uint8Array ? c : new Uint8Array(Array.from(c));
              const imgKind = detectImageAtStart(u8);
              if (imgKind) {
                renderImageBlobBytes(u8, imgKind.mime);
                return;
              }
              if (u8[0] === 0x42 && u8[1] === 0x4D && u8.length >= 54) {
                bmpBytes = u8;
              } else {
                // /CONTENTS BMP veya tanınan resim değil — Package gibi taşıyıcı olabilir
                const scanned = findImageInBytes(u8, Math.min(u8.length, 8192));
                if (scanned) {
                  renderImageBlobBytes(scanned.bytes, scanned.mime);
                  return;
                }
              }
            }
            if (!bmpBytes) {
              const oleNativeEntry = CFB.find(cfbParsed, '/\x01Ole10Native');
              if (oleNativeEntry?.content?.length > 4) {
                const raw = oleNativeEntry.content;
                // İlk 4 byte = size field (atla)
                const dibRaw = raw.slice(4);
                const dibU8  = dibRaw instanceof Uint8Array ? dibRaw : new Uint8Array(Array.from(dibRaw));

                // PNG/JPG doğrudan olabilir (4-byte size sonrası)
                const imgKindN = detectImageAtStart(dibU8);
                if (imgKindN) {
                  renderImageBlobBytes(dibU8, imgKindN.mime);
                  return;
                }

                if (dibU8[0] === 0x42 && dibU8[1] === 0x4D) {
                  // Zaten tam BMP dosyası
                  bmpBytes = dibU8;
                } else {
                  // Package taşıyıcı? PNG/JPG'yi içeride ara
                  const scannedN = findImageInBytes(dibU8, Math.min(dibU8.length, 8192));
                  if (scannedN) {
                    renderImageBlobBytes(scannedN.bytes, scannedN.mime);
                    return;
                  }
                  // Sadece DIB — önüne BITMAPFILEHEADER ekle
                  const dv = new DataView(dibU8.buffer, dibU8.byteOffset);
                  const dibHeaderSize     = dv.getUint32(0, true);
                  const biBitCount        = dv.getUint16(14, true);
                  const colorTableEntries = biBitCount <= 8 ? (1 << biBitCount) : 0;
                  const pixelDataOffset   = 14 + dibHeaderSize + colorTableEntries * 4;
                  const totalSize         = 14 + dibU8.length;

                  bmpBytes = new Uint8Array(totalSize);
                  const fv = new DataView(bmpBytes.buffer);
                  bmpBytes[0] = 0x42; bmpBytes[1] = 0x4D;
                  fv.setUint32(2,  totalSize,       true);
                  fv.setUint16(6,  0,               true);
                  fv.setUint16(8,  0,               true);
                  fv.setUint32(10, pixelDataOffset, true);
                  bmpBytes.set(dibU8, 14);
                }
              }
            }

            // Format 3: /\x02OlePres000 (OLE 2.0 sunum — CF_DIB ise bitmap içerir)
            if (!bmpBytes) {
              const presEntry = CFB.find(cfbParsed, '/\x02OlePres000');
              if (presEntry?.content?.length > 32) {
                const pRaw = presEntry.content instanceof Uint8Array
                  ? presEntry.content : new Uint8Array(Array.from(presEntry.content));
                const pDv = new DataView(pRaw.buffer, pRaw.byteOffset);
                // Sabit başlık 24 byte: lindex+reserved+dwAdvf+reserved+extX+extY
                const markerOrLength = pDv.getUint32(24, true);
                if (markerOrLength === 8 /* CF_DIB */) {
                  const cbSize   = pDv.getUint32(28, true);
                  const dibStart = 32;
                  if (cbSize > 0 && dibStart + cbSize <= pRaw.length) {
                    const dibRaw = pRaw.slice(dibStart, dibStart + cbSize);
                    if (dibRaw[0] === 0x42 && dibRaw[1] === 0x4D) {
                      bmpBytes = dibRaw;
                    } else {
                      const dv2          = new DataView(dibRaw.buffer, dibRaw.byteOffset);
                      const dibHdrSize2  = dv2.getUint32(0, true);
                      const biBitCount2  = dv2.getUint16(14, true);
                      const colorTable2  = biBitCount2 <= 8 ? (1 << biBitCount2) : 0;
                      const pixelOff2    = 14 + dibHdrSize2 + colorTable2 * 4;
                      const totalSize2   = 14 + dibRaw.length;
                      bmpBytes = new Uint8Array(totalSize2);
                      const fv2 = new DataView(bmpBytes.buffer);
                      bmpBytes[0] = 0x42; bmpBytes[1] = 0x4D;
                      fv2.setUint32(2,  totalSize2, true);
                      fv2.setUint16(6,  0,          true);
                      fv2.setUint16(8,  0,          true);
                      fv2.setUint32(10, pixelOff2,  true);
                      bmpBytes.set(dibRaw, 14);
                    }
                  }
                }
              }
            }

            // Format 4: Brute-force — tüm CFB stream'lerinde BMP/DIB başlığı ara
            if (!bmpBytes) {
              for (const entry of (cfbParsed.FileIndex || [])) {
                const ec = entry?.content;
                if (!ec || ec.length < 40) continue;
                const ecU8 = ec instanceof Uint8Array ? ec : new Uint8Array(Array.from(ec));
                if (ecU8.length >= 54 && ecU8[0] === 0x42 && ecU8[1] === 0x4D) {
                  bmpBytes = ecU8;
                  break;
                }
                const ecDv    = new DataView(ecU8.buffer, ecU8.byteOffset);
                const hdrSize = ecDv.getUint32(0, true);
                if (hdrSize === 40) {
                  const w = ecDv.getInt32(4, true);
                  const h = ecDv.getInt32(8, true);
                  if (w > 0 && w < 32768 && Math.abs(h) > 0 && Math.abs(h) < 32768) {
                    const biBitCount3  = ecDv.getUint16(14, true);
                    const colorTable3  = biBitCount3 <= 8 ? (1 << biBitCount3) : 0;
                    const pixelOff3    = 14 + hdrSize + colorTable3 * 4;
                    const totalSize3   = 14 + ecU8.length;
                    bmpBytes = new Uint8Array(totalSize3);
                    const fv3 = new DataView(bmpBytes.buffer);
                    bmpBytes[0] = 0x42; bmpBytes[1] = 0x4D;
                    fv3.setUint32(2,  totalSize3, true);
                    fv3.setUint16(6,  0,          true);
                    fv3.setUint16(8,  0,          true);
                    fv3.setUint32(10, pixelOff3,  true);
                    bmpBytes.set(ecU8, 14);
                    break;
                  }
                }
              }
            }

            if (!bmpBytes || bmpBytes.length < 54) {
              // Gömülü .xls (BIFF) Workbook stream'inden, aktif sayfanın SELECTION
              // record'unu okuyarak kullanıcının Excel'de seçtiği aralığı bul.
              // BIFF8 SELECTION (recId=0x001D): pnn(1) rwAct(2) colAct(2) irefAct(2)
              // cref(2) REF8U[cref] (her biri 8B: rwFirst, rwLast, colFirst, colLast).
              // BOF(0x0809) record'larıyla worksheet substream'lerini sayarak aktif
              // sayfanın indexine eşleşeni alıyoruz.
              const findBiffSelection = (xlsBytes, activeIdx) => {
                if (!xlsBytes || xlsBytes.length < 8) return null;
                if (xlsBytes[0] !== 0xD0 || xlsBytes[1] !== 0xCF) return null; // CFB değil
                let inner;
                try { inner = CFB.read(xlsBytes, { type: 'array' }); }
                catch (e) { return null; }
                const wbE = CFB.find(inner, '/Workbook') || CFB.find(inner, '/Book');
                if (!wbE?.content) return null;
                const wbU8 = wbE.content instanceof Uint8Array
                  ? wbE.content : new Uint8Array(Array.from(wbE.content));
                if (wbU8.length < 8) return null;
                const dv = new DataView(wbU8.buffer, wbU8.byteOffset, wbU8.byteLength);

                let pos = 0, sheetIdx = -1, found = null;
                while (pos + 4 <= wbU8.length) {
                  const recId  = dv.getUint16(pos, true);
                  const recLen = dv.getUint16(pos + 2, true);
                  if (pos + 4 + recLen > wbU8.length) break;
                  const dp = pos + 4;

                  if (recId === 0x0809 /* BOF */ && recLen >= 4) {
                    const dt = dv.getUint16(dp + 2, true);
                    if (dt === 0x0010) sheetIdx++; // worksheet substream
                  }
                  else if (recId === 0x001D /* SELECTION */ &&
                           sheetIdx === activeIdx && recLen >= 9) {
                    const cref = dv.getUint16(dp + 7, true);
                    if (cref >= 1 && recLen >= 17) {
                      const refOff = dp + 9;
                      const rwFirst  = dv.getUint16(refOff + 0, true);
                      const rwLast   = dv.getUint16(refOff + 2, true);
                      const colFirst = dv.getUint16(refOff + 4, true);
                      const colLast  = dv.getUint16(refOff + 6, true);
                      // Sayfa içinde birden fazla pane varsa son SELECTION'u tut
                      // (genelde aktif pane = pnn 3'ün SELECTION'u en son yazılır)
                      found = { rwFirst, rwLast, colFirst, colLast };
                    }
                  }
                  pos += 4 + recLen;
                }
                return found;
              };

              // ---- helpers (closure üzerinden oleMesh/oleTexture/oleCanvas'ı yakalar) ----
              const showOleLabel = (text) => {
                const c = oleCanvas.getContext('2d');
                c.clearRect(0, 0, oleCanvas.width, oleCanvas.height);
                c.fillStyle = '#d0d0d0';
                c.fillRect(0, 0, oleCanvas.width, oleCanvas.height);
                c.fillStyle = '#666666';
                c.font = 'bold 48px sans-serif';
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText(text || 'OLE', oleCanvas.width / 2, oleCanvas.height / 2);
                oleTexture.needsUpdate = true;
              };

              const renderXlsxBytes = (bytes) =>
                import('xlsx').then(mod => {
                  const XLSX = mod.default || mod;
                  const wb = XLSX.read(bytes, { type: 'array', cellStyles: true, bookVBA: false });
                  const sheetNames = wb.SheetNames || [];
                  if (!sheetNames.length) throw new Error('SheetNames boş');

                  // Aktif sekme: kaydedildiğinde son seçili olan sayfa.
                  // Excel bunu Workbook.WBView[*].activeTab içinde tutar.
                  let activeIdx = wb.Workbook?.WBView?.[0]?.activeTab;
                  if (typeof activeIdx !== 'number' || activeIdx < 0 || activeIdx >= sheetNames.length) {
                    activeIdx = 0;
                  }
                  // Aktif sayfa boşsa veya gizliyse, ilk uygun sayfaya düş
                  const wbSheets = wb.Workbook?.Sheets || [];
                  const isUsable = (i) => {
                    const s = wb.Sheets[sheetNames[i]];
                    if (!s || !s['!ref']) return false;
                    const hidden = wbSheets[i]?.Hidden;
                    return !hidden; // 0 = visible, 1 = hidden, 2 = veryHidden
                  };
                  if (!isUsable(activeIdx)) {
                    const alt = sheetNames.findIndex((_, i) => isUsable(i));
                    if (alt >= 0) activeIdx = alt;
                  }

                  const wsName = sheetNames[activeIdx];
                  const ws = wb.Sheets[wsName];
                  if (!ws || !ws['!ref']) throw new Error('!ref yok');

                  // Kullanıcının Excel'de seçtiği aralığı bul. Tercih sırası:
                  //   1) BIFF SELECTION record (gömülü .xls için en güvenilir)
                  //   2) Print_Area defined name
                  //   3) autofilter ref
                  // Hiçbiri yoksa pixel-bazlı kırpma fallback olarak çalışır.
                  let selectionRef = null;

                  // 1) BIFF SELECTION (sadece .xls / CFB için)
                  try {
                    const biffSel = findBiffSelection(bytes, activeIdx);
                    if (biffSel) {
                      selectionRef = XLSX.utils.encode_range({
                        s: { r: biffSel.rwFirst, c: biffSel.colFirst },
                        e: { r: biffSel.rwLast,  c: biffSel.colLast  },
                      });
                    }
                  } catch (e) {
                    console.warn('[OLE2FRAME] BIFF selection parse hatası:', e);
                  }

                  // 2) Print_Area
                  if (!selectionRef) {
                    const names = wb.Workbook?.Names || [];
                    for (const n of names) {
                      if (!n?.Ref || !n?.Name) continue;
                      const baseName = n.Name.replace(/^_xlnm\./, '').toLowerCase();
                      if (baseName !== 'print_area') continue;
                      if (n.Sheet != null && n.Sheet !== activeIdx) continue;
                      const refPart = (n.Ref.split('!').pop() || '').replace(/\$/g, '');
                      if (refPart && /^[A-Z]+\d+:[A-Z]+\d+$/i.test(refPart)) {
                        selectionRef = refPart;
                        break;
                      }
                    }
                  }

                  // 3) autofilter
                  if (!selectionRef && ws['!autofilter']?.ref) {
                    selectionRef = ws['!autofilter'].ref;
                  }

                  let range;
                  if (selectionRef) {
                    try {
                      range = XLSX.utils.decode_range(selectionRef);
                    } catch (e) {
                      range = XLSX.utils.decode_range(ws['!ref']);
                      selectionRef = null;
                    }
                  } else {
                    range = XLSX.utils.decode_range(ws['!ref']);
                  }
                  let nRows     = range.e.r - range.s.r + 1;
                  let nCols     = range.e.c - range.s.c + 1;
                  const COL_DEF = 70, ROW_DEF = 22;
                  let colWidths = Array.from({ length: nCols }, (_, i) => {
                    const ci = (ws['!cols'] || [])[range.s.c + i];
                    return ci?.wpx || (ci?.wch ? Math.round(ci.wch * 7) : COL_DEF);
                  });
                  let rowHeights = Array.from({ length: nRows }, (_, i) => {
                    const ri = (ws['!rows'] || [])[range.s.r + i];
                    return ri?.hpx || (ri?.hpt ? Math.round(ri.hpt * 4 / 3) : ROW_DEF);
                  });

                  // Print_Area gibi açık bir seçim referansı varsa pixel-bazlı kırpmayı
                  // atla — aralık zaten gerçek hücre koordinatları üzerinden geldi.
                  // Yoksa OLE çerçevesinin görünür alanı kadar sütun/satır al.
                  if (!selectionRef && viewPxW > 0 && viewPxH > 0) {
                    const TOL = 1.05;
                    let limCols = nCols, accCw = 0;
                    for (let i = 0; i < nCols; i++) {
                      accCw += colWidths[i];
                      if (accCw > viewPxW * TOL && i + 1 < nCols) { limCols = i + 1; break; }
                    }
                    let limRows = nRows, accRh = 0;
                    for (let i = 0; i < nRows; i++) {
                      accRh += rowHeights[i];
                      if (accRh > viewPxH * TOL && i + 1 < nRows) { limRows = i + 1; break; }
                    }
                    if (limCols < nCols || limRows < nRows) {
                      nCols       = limCols;
                      nRows       = limRows;
                      colWidths   = colWidths.slice(0, limCols);
                      rowHeights  = rowHeights.slice(0, limRows);
                      range.e.c   = range.s.c + limCols - 1;
                      range.e.r   = range.s.r + limRows - 1;
                    }
                  }

                  const totalW = colWidths.reduce((a, b) => a + b, 0);
                  const totalH = rowHeights.reduce((a, b) => a + b, 0);
                  const MAX_DIM = 2048;
                  const scale = Math.min(2, MAX_DIM / Math.max(totalW, totalH, 1));
                  const colX = [0];
                  for (let i = 0; i < nCols; i++) colX.push(colX[i] + colWidths[i] * scale);
                  const rowY = [0];
                  for (let i = 0; i < nRows; i++) rowY.push(rowY[i] + rowHeights[i] * scale);
                  const canvasW = Math.min(Math.ceil(totalW * scale), MAX_DIM);
                  const canvasH = Math.min(Math.ceil(totalH * scale), MAX_DIM);
                  const exC = document.createElement('canvas');
                  exC.width = canvasW; exC.height = canvasH;
                  const ctx = exC.getContext('2d');
                  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvasW, canvasH);

                  const hidden = new Set(), merges = {};
                  for (const m of (ws['!merges'] || [])) {
                    merges[`${m.s.r},${m.s.c}`] = m;
                    for (let mr = m.s.r; mr <= m.e.r; mr++)
                      for (let mc = m.s.c; mc <= m.e.c; mc++)
                        if (mr !== m.s.r || mc !== m.s.c) hidden.add(`${mr},${mc}`);
                  }
                  const baseFs = Math.max(8, Math.round(11 * scale));

                  for (let r = range.s.r; r <= range.e.r; r++) {
                    const ri = r - range.s.r;
                    for (let c = range.s.c; c <= range.e.c; c++) {
                      const ci = c - range.s.c;
                      if (hidden.has(`${r},${c}`)) continue;
                      const m = merges[`${r},${c}`];
                      const x = colX[ci], y = rowY[ri];
                      const w = m ? colX[m.e.c - range.s.c + 1] - x : colWidths[ci] * scale;
                      const h = m ? rowY[m.e.r - range.s.r + 1] - y : rowHeights[ri] * scale;
                      const cell = ws[XLSX.utils.encode_cell({ r, c })];
                      const s = cell?.s || {};

                      const bg = s.fgColor?.rgb || s.bgColor?.rgb;
                      ctx.fillStyle = (bg && bg !== 'FFFFFF' && bg !== 'ffffff') ? '#' + bg : '#ffffff';
                      ctx.fillRect(x, y, w, h);
                      ctx.strokeStyle = '#aaaaaa'; ctx.lineWidth = 1;
                      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

                      const drawBorder = (side, x1, y1, x2, y2) => {
                        const b = s.border?.[side];
                        if (!b?.style || b.style === 'none') return;
                        ctx.strokeStyle = b.color?.rgb ? '#' + b.color.rgb : '#000000';
                        ctx.lineWidth = b.style === 'thick' ? 3 : b.style === 'medium' ? 2 : 1.5;
                        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
                      };
                      drawBorder('top',    x,     y,     x + w, y);
                      drawBorder('right',  x + w, y,     x + w, y + h);
                      drawBorder('bottom', x,     y + h, x + w, y + h);
                      drawBorder('left',   x,     y,     x,     y + h);

                      if (!cell) continue;
                      const txt = cell.w != null ? String(cell.w) : (cell.v != null ? String(cell.v) : '');
                      if (!txt) continue;
                      const fsPx = s.font?.sz ? Math.round(s.font.sz * scale * 1.33) : baseFs;
                      ctx.font = `${s.font?.italic ? 'italic ' : ''}${s.font?.bold ? 'bold ' : ''}${fsPx}px Arial, sans-serif`;
                      const fc = s.font?.color?.rgb;
                      ctx.fillStyle = (fc && fc.toUpperCase() !== 'FFFFFF') ? '#' + fc : '#000000';
                      const ha = s.alignment?.horizontal || (typeof cell.v === 'number' ? 'right' : 'left');
                      ctx.textBaseline = 'middle';
                      ctx.textAlign = ha === 'center' ? 'center' : ha === 'right' ? 'right' : 'left';
                      const pad = Math.max(2, 3 * scale);
                      const tx = ha === 'center' ? x + w / 2 : ha === 'right' ? x + w - pad : x + pad;
                      ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
                      ctx.fillText(txt, tx, y + h / 2); ctx.restore();
                    }
                  }

                  applyOleTexture(new THREE.CanvasTexture(exC));
                });

              const renderImageBytes = (bytes, mime) => new Promise((resolve, reject) => {
                const blob = new Blob([bytes], { type: mime });
                const url  = URL.createObjectURL(blob);
                const img  = new Image();
                img.onload = () => {
                  const fc = document.createElement('canvas');
                  fc.width  = img.naturalWidth  || 512;
                  fc.height = img.naturalHeight || 512;
                  fc.getContext('2d').drawImage(img, 0, 0);
                  applyOleTexture(new THREE.CanvasTexture(fc));
                  URL.revokeObjectURL(url);
                  resolve();
                };
                img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
                img.src = url;
              });

              const renderPdfBytes = (bytes) =>
                import('pdfjs-dist').then(async pdfjs => {
                  if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
                    try {
                      const w = await import('pdfjs-dist/build/pdf.worker.entry');
                      pdfjs.GlobalWorkerOptions.workerSrc = w.default || w;
                    } catch (e) { /* main thread fallback */ }
                  }
                  const pdf  = await pdfjs.getDocument({ data: bytes }).promise;
                  const page = await pdf.getPage(1);
                  const viewport = page.getViewport({ scale: 2 });
                  const fc = document.createElement('canvas');
                  fc.width  = viewport.width;
                  fc.height = viewport.height;
                  await page.render({ canvasContext: fc.getContext('2d'), viewport }).promise;
                  applyOleTexture(new THREE.CanvasTexture(fc));
                });

              // ---- Format 5: Excel Workbook stream (gerçek .xls embed) ----
              let wbEntry = CFB.find(cfbParsed, '/Workbook') || CFB.find(cfbParsed, '/Book');
              if (!wbEntry) {
                for (const e of (cfbParsed.FileIndex || [])) {
                  const n = (e?.name || '').toLowerCase();
                  if (n === 'workbook' || n === 'book' ||
                      n.endsWith('/workbook') || n.endsWith('/book')) {
                    wbEntry = e; break;
                  }
                }
              }
              if (wbEntry?.content?.length > 0) {
                const wbU8 = wbEntry.content instanceof Uint8Array
                  ? wbEntry.content : new Uint8Array(Array.from(wbEntry.content));
                renderXlsxBytes(wbU8).catch(err => {
                  console.warn('[OLE2FRAME] Workbook render hatası:', err);
                  showOleLabel('XLS');
                });
                return;
              }

              // ---- Format 6: /Package — gömülü dosya (xlsx, pdf, png, jpg, doc, docx, xls) ----
              let pkgEntry = CFB.find(cfbParsed, '/Package');
              if (!pkgEntry) {
                for (const e of (cfbParsed.FileIndex || [])) {
                  const n = (e?.name || '').toLowerCase();
                  if (n === 'package' || n.endsWith('/package')) { pkgEntry = e; break; }
                }
              }
              if (pkgEntry?.content?.length > 16) {
                const pkgU8 = pkgEntry.content instanceof Uint8Array
                  ? pkgEntry.content : new Uint8Array(Array.from(pkgEntry.content));

                // Bilinen dosya magic'leri — Package header'ı atlayıp asıl içeriği bul
                const magics = [
                  { sig: [0x25, 0x50, 0x44, 0x46],                         type: 'pdf' }, // %PDF
                  { sig: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], type: 'png' },
                  { sig: [0xFF, 0xD8, 0xFF],                               type: 'jpg' },
                  { sig: [0x50, 0x4B, 0x03, 0x04],                         type: 'zip' }, // xlsx/docx
                  { sig: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], type: 'cfb' }, // xls/doc
                ];

                let foundType = null, foundOff = -1, foundSize = -1;
                outer: for (let pi = 0; pi <= pkgU8.length - 4; pi++) {
                  for (const m of magics) {
                    if (pi + m.sig.length > pkgU8.length) continue;
                    let ok = true;
                    for (let k = 0; k < m.sig.length; k++) {
                      if (pkgU8[pi + k] !== m.sig[k]) { ok = false; break; }
                    }
                    if (ok) {
                      // Microsoft Package header'da magic'ten 4 byte önce dosya boyutu yer alır
                      if (pi >= 4) {
                        const dv = new DataView(pkgU8.buffer, pkgU8.byteOffset + pi - 4, 4);
                        const sz = dv.getUint32(0, true);
                        if (sz > m.sig.length && sz <= pkgU8.length - pi) foundSize = sz;
                      }
                      if (foundSize === -1) foundSize = pkgU8.length - pi;
                      foundType = m.type;
                      foundOff  = pi;
                      break outer;
                    }
                  }
                }

                if (foundType) {
                  const fileBytes = pkgU8.slice(foundOff, foundOff + foundSize);

                  if (foundType === 'png' || foundType === 'jpg') {
                    renderImageBytes(fileBytes, foundType === 'png' ? 'image/png' : 'image/jpeg')
                      .catch(err => {
                        console.warn('[OLE2FRAME] Resim render hatası:', err);
                        showOleLabel(foundType.toUpperCase());
                      });
                    return;
                  }

                  if (foundType === 'pdf') {
                    renderPdfBytes(fileBytes).catch(err => {
                      console.warn('[OLE2FRAME] PDF render hatası:', err);
                      showOleLabel('PDF');
                    });
                    return;
                  }

                  if (foundType === 'zip' || foundType === 'cfb') {
                    // xlsx hem .xlsx (zip) hem .xls (CFB) okuyabilir
                    renderXlsxBytes(fileBytes).catch(err => {
                      // Word olabilir — mammoth ile dene (varsa)
                      console.warn('[OLE2FRAME] xlsx olarak okunamadı, Word denenecek:', err?.message || err);
                      import('mammoth').then(m => {
                        const mammoth = m.default || m;
                        return mammoth.convertToHtml({ arrayBuffer: fileBytes.buffer.slice(
                          fileBytes.byteOffset, fileBytes.byteOffset + fileBytes.byteLength) });
                      }).then(result => {
                        const html = result?.value || '';
                        if (!html) throw new Error('mammoth: boş html');
                        // HTML'i geçici bir DOM elemanında render edip canvas'a çiz
                        const wrap = document.createElement('div');
                        wrap.style.cssText = 'position:fixed;left:-99999px;top:0;width:1024px;background:#fff;padding:24px;font-family:Arial,sans-serif;font-size:14px;color:#000;';
                        wrap.innerHTML = html;
                        document.body.appendChild(wrap);
                        const fc = document.createElement('canvas');
                        fc.width  = 1024;
                        fc.height = Math.min(2048, Math.max(512, wrap.offsetHeight));
                        const ctx = fc.getContext('2d');
                        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, fc.width, fc.height);
                        // Basit metin render — DOM'u canvas'a tam çizmek zor, satır satır metin dök
                        ctx.fillStyle = '#000000';
                        ctx.font = '14px Arial, sans-serif';
                        ctx.textBaseline = 'top';
                        const text = (wrap.innerText || wrap.textContent || '').replace(/\s+\n/g, '\n');
                        document.body.removeChild(wrap);
                        const lines = text.split('\n');
                        let y = 24;
                        for (const line of lines) {
                          if (y > fc.height - 20) break;
                          // Uzun satırları kır
                          const words = line.split(' ');
                          let cur = '';
                          for (const w of words) {
                            const test = cur ? cur + ' ' + w : w;
                            if (ctx.measureText(test).width > fc.width - 48) {
                              ctx.fillText(cur, 24, y); y += 18; cur = w;
                              if (y > fc.height - 20) break;
                            } else {
                              cur = test;
                            }
                          }
                          if (cur && y <= fc.height - 20) { ctx.fillText(cur, 24, y); y += 18; }
                        }
                        applyOleTexture(new THREE.CanvasTexture(fc));
                      }).catch(merr => {
                        console.warn('[OLE2FRAME] Word render hatası:', merr);
                        showOleLabel(foundType === 'cfb' ? 'DOC' : 'DOCX');
                      });
                    });
                    return;
                  }
                }
              }

              // ---- Son çare: tüm CFB blob'unu xlsx.read'e ver ----
              const _cfbBlob = binary.slice(cfbOff);
              renderXlsxBytes(_cfbBlob).catch(err => {
                console.warn('[OLE2FRAME] xlsx full-blob fallback başarısız:', err?.message || err);
                showOleLabel('OLE');
              });
              return;
            }

            // Manuel BMP decode — <img> ile blob:image/bmp bazı Chrome sürümlerinde
            // sessizce başarısız olabiliyor (24-bit RGB standart bile olsa). Önce
            // 24-bit uncompressed BMP'yi elle decode et; başarısızsa <img> fallback.
            const decodeBmp24ToCanvas = (u8) => {
              if (!u8 || u8.length < 54 || u8[0] !== 0x42 || u8[1] !== 0x4D) return null;
              const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
              const dataOff = dv.getUint32(10, true);
              const w = dv.getInt32(18, true);
              let h = dv.getInt32(22, true);
              const bpp = dv.getUint16(28, true);
              const comp = dv.getUint32(30, true);
              if (w <= 0 || w > 8192 || Math.abs(h) === 0 || Math.abs(h) > 8192) return null;
              if (bpp !== 24 || comp !== 0) return null;
              const topDown = h < 0;
              h = Math.abs(h);
              const rowSize = Math.floor((bpp * w + 31) / 32) * 4;
              if (dataOff + rowSize * h > u8.length) return null;
              const canvas = document.createElement('canvas');
              canvas.width = w; canvas.height = h;
              const ctx = canvas.getContext('2d');
              const imgData = ctx.createImageData(w, h);
              const out = imgData.data;
              for (let y = 0; y < h; y++) {
                const srcY = topDown ? y : (h - 1 - y);
                const srcRow = dataOff + srcY * rowSize;
                const dstRow = y * w * 4;
                for (let x = 0; x < w; x++) {
                  const s = srcRow + x * 3;
                  const d = dstRow + x * 4;
                  out[d]     = u8[s + 2]; // R ← BGR
                  out[d + 1] = u8[s + 1];
                  out[d + 2] = u8[s];
                  out[d + 3] = 255;
                }
              }
              ctx.putImageData(imgData, 0, 0);
              return canvas;
            };

            const manualCanvas = decodeBmp24ToCanvas(bmpBytes);
            if (manualCanvas) {
              const finalTexture = new THREE.CanvasTexture(manualCanvas);
              applyOleTexture(finalTexture);
            } else {
              // Fallback: <img> ile blob — manuel decoder desteklemediği BMP varyantları için
              const bmpBlob = new Blob([bmpBytes], { type: 'image/bmp' });
              const bmpUrl  = URL.createObjectURL(bmpBlob);
              const oleImg  = new Image();
              oleImg.onload = () => {
                const finalCanvas = document.createElement('canvas');
                finalCanvas.width  = oleImg.naturalWidth  || 256;
                finalCanvas.height = oleImg.naturalHeight || 256;
                finalCanvas.getContext('2d').drawImage(oleImg, 0, 0);
                const finalTexture = new THREE.CanvasTexture(finalCanvas);
                applyOleTexture(finalTexture);
                URL.revokeObjectURL(bmpUrl);
              };
              oleImg.onerror = () => {
                console.warn('[OLE2FRAME] BMP fallback decode başarısız');
                URL.revokeObjectURL(bmpUrl);
              };
              oleImg.src = bmpUrl;
            }
          } catch (oleErr) {
            console.warn('OLE2FRAME render hatası:', oleErr);
          }
        }, 0);

        break;
      }

      default:
      // console.warn(`dxfDrawCore: Tanınmayan type: ${type}`);
    }
  });
  return drawingGroup;
}


function classifyBoundaryPaths(boundaryPaths) {
  const clonedPaths = boundaryPaths.map(path => ({ ...path }));

  // Her boundary için bbox hesapla ve innerGroups dizisi oluştur
  clonedPaths.forEach((boundary) => {
    boundary.innerGroups = [];
    boundary.bbox = calculateBoundingBox(boundary.groups);
  });

  // Her boundary için en küçük kapsayıcı (immediate parent) bul
  for (let i = 0; i < clonedPaths.length; i++) {
    const bboxA = clonedPaths[i].bbox;
    const areaA = bboxA.width * bboxA.height;
    let smallestContainer = null;
    let smallestContainerArea = Infinity;

    for (let j = 0; j < clonedPaths.length; j++) {
      if (i === j) continue;
      const bboxB = clonedPaths[j].bbox;

      // A, B'nin içinde mi?
      if (isContained(bboxA, bboxB)) {
        const areaB = bboxB.width * bboxB.height;
        // CYCLE KORUMASI: Eşit/örtüşen bbox'larda (A⊂B ve B⊂A ikisi de doğru)
        // iki boundary birbirini innerGroups'a ekleyip DAİRESEL yapı kuruyordu
        // → JSON.stringify "Converting circular structure" ile patlıyordu.
        // B ancak KESİN büyükse kapsayıcıdır; alan tam eşitse index ile simetri
        // kır (yalnız j<i tarafı kapsayan sayılır) → cycle imkânsız (DAG).
        const bContainsA = areaB > areaA || (areaB === areaA && j < i);
        // En küçük kapsayıcıyı bul (immediate parent)
        if (bContainsA && areaB < smallestContainerArea) {
          smallestContainerArea = areaB;
          smallestContainer = j;
        }
      }
    }

    if (smallestContainer !== null) {
      // Bu boundary, smallestContainer'ın içinde
      clonedPaths[smallestContainer].innerGroups.push(clonedPaths[i]);
      clonedPaths[i].type = "inner";
      clonedPaths[smallestContainer].type = "outer";
    } else {
      // Hiçbir şeyin içinde değil - outer
      clonedPaths[i].type = "outer";
    }
  }

  return clonedPaths;
}



function calculateBoundingBox(result) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  result.forEach(item => {
    // DXF parser edgeType kullanıyor, eski format type kullanıyor - ikisini de destekle
    const itemType = (item.edgeType || item.type || '').toLowerCase();

    switch (itemType) {
      case "line":
        // DXF parser format: x1, y1, x2, y2
        if (item.x1 !== undefined) {
          minX = Math.min(minX, item.x1, item.x2);
          minY = Math.min(minY, item.y1, item.y2);
          maxX = Math.max(maxX, item.x1, item.x2);
          maxY = Math.max(maxY, item.y1, item.y2);
        }
        // Eski format: vertices array
        else if (item.vertices) {
          item.vertices.forEach(vertex => {
            minX = Math.min(minX, vertex.x);
            minY = Math.min(minY, vertex.y);
            maxX = Math.max(maxX, vertex.x);
            maxY = Math.max(maxY, vertex.y);
          });
        }
        // points array (bulge ile)
        else if (item.points) {
          // ✅ FIX: bulge varsa stride-3 [X,Y,Bulge,...], yoksa stride-2 [X,Y,...]
          const stride = item.bulge ? 3 : 2;
          for (let i = 0; i < item.points.length; i += stride) {
            minX = Math.min(minX, item.points[i]);
            minY = Math.min(minY, item.points[i + 1]);
            maxX = Math.max(maxX, item.points[i]);
            maxY = Math.max(maxY, item.points[i + 1]);
          }
        }
        break;

      case "polyline":
      case "lwpolyline":
        // DXF parser format: points array
        if (item.points) {
          // ✅ FIX: bulge varsa stride-3 [X,Y,Bulge,...], yoksa stride-2 [X,Y,...]
          const pStride = item.bulge ? 3 : 2;
          for (let i = 0; i < item.points.length; i += pStride) {
            minX = Math.min(minX, item.points[i]);
            minY = Math.min(minY, item.points[i + 1]);
            maxX = Math.max(maxX, item.points[i]);
            maxY = Math.max(maxY, item.points[i + 1]);
          }
        }
        // Eski format: vertices array
        else if (item.vertices) {
          item.vertices.forEach(vertex => {
            minX = Math.min(minX, vertex.x);
            minY = Math.min(minY, vertex.y);
            maxX = Math.max(maxX, vertex.x);
            maxY = Math.max(maxY, vertex.y);
          });
        }
        break;

      case "circulararc":
      case "arc":
        // DXF parser format: x, y, radius
        const arcCenterX = item.x ?? item.center?.x ?? 0;
        const arcCenterY = item.y ?? item.center?.y ?? 0;
        const arcRadius = item.radius ?? 0;
        minX = Math.min(minX, arcCenterX - arcRadius);
        minY = Math.min(minY, arcCenterY - arcRadius);
        maxX = Math.max(maxX, arcCenterX + arcRadius);
        maxY = Math.max(maxY, arcCenterY + arcRadius);
        break;

      case "ellipticarc":
      case "ellipse":
        // Elips için yaklaşık bounding box
        const ellipseCenterX = item.x ?? item.center?.x ?? 0;
        const ellipseCenterY = item.y ?? item.center?.y ?? 0;
        const majorRadius = Math.sqrt((item.EndPointX || 0) ** 2 + (item.EndPointY || 0) ** 2);
        const minorRadius = majorRadius * (item.ratioOfMinor || 1);
        const maxRadius = Math.max(majorRadius, minorRadius);
        minX = Math.min(minX, ellipseCenterX - maxRadius);
        minY = Math.min(minY, ellipseCenterY - maxRadius);
        maxX = Math.max(maxX, ellipseCenterX + maxRadius);
        maxY = Math.max(maxY, ellipseCenterY + maxRadius);
        break;

      case "spline":
        // Spline için control points kullan
        if (item.points) {
          for (let i = 0; i < item.points.length; i += 2) {
            minX = Math.min(minX, item.points[i]);
            minY = Math.min(minY, item.points[i + 1]);
            maxX = Math.max(maxX, item.points[i]);
            maxY = Math.max(maxY, item.points[i + 1]);
          }
        }
        break;

      case "circle":
        const circleCenterX = item.x ?? item.center?.x ?? 0;
        const circleCenterY = item.y ?? item.center?.y ?? 0;
        const circleRadius = item.radius ?? 0;
        minX = Math.min(minX, circleCenterX - circleRadius);
        minY = Math.min(minY, circleCenterY - circleRadius);
        maxX = Math.max(maxX, circleCenterX + circleRadius);
        maxY = Math.max(maxY, circleCenterY + circleRadius);
        break;
    }
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
} function isContained(bboxA, bboxB) {
  // bboxA'nın bboxB'nin içinde olup olmadığını kontrol eder
  return bboxA.minX >= bboxB.minX && bboxA.maxX <= bboxB.maxX &&
    bboxA.minY >= bboxB.minY && bboxA.maxY <= bboxB.maxY;
}
function isDisjoint(bboxA, bboxB) {
  // İki bounding box'ın kesişip kesişmediğini kontrol eder (disjoint olup olmadığını)
  return bboxA.maxX < bboxB.minX || bboxA.minX > bboxB.maxX ||
    bboxA.maxY < bboxB.minY || bboxA.minY > bboxB.maxY;
}
function isIntersecting(bboxA, bboxB) {
  return !isDisjoint(bboxA, bboxB) && !isContained(bboxA, bboxB) && !isContained(bboxB, bboxA);
}